// src/components/property/video-settings/ManageTemplatesTab.tsx
/**
 * ManageTemplatesTab Component
 * Tab for enabling/disabling system templates
 *
 * Feature: Video Settings
 */

import { useMemo } from 'react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { IManageTemplatesTabProps, TemplateCategory } from '@/@types/videoSettings';
import type { IPromptTemplate } from '@/@types/promptTemplates';

const CATEGORY_IMAGE_TYPES: Record<TemplateCategory, string[]> = {
  building: ['building'],
  outdoor: ['backyard', 'pool', 'garden', 'parking', 'garage', 'balcony'],
  interior: ['living_room', 'kitchen', 'bedroom', 'bathroom'],
  other: ['plan', 'other'],
};

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  building: 'Building',
  outdoor: 'Outdoor',
  interior: 'Interior',
  other: 'Other',
};

function ManageTemplatesTab({
  systemTemplates,
  excludedIds,
  onToggleExclusion,
  isLoading,
}: IManageTemplatesTabProps) {
  // Group templates by category
  const templatesByCategory = useMemo(() => {
    const groups: Record<TemplateCategory, IPromptTemplate[]> = {
      building: [],
      outdoor: [],
      interior: [],
      other: [],
    };

    for (const template of systemTemplates) {
      for (const [category, imageTypes] of Object.entries(CATEGORY_IMAGE_TYPES)) {
        if (template.imageTypes.some(it => imageTypes.includes(it))) {
          groups[category as TemplateCategory].push(template);
          break; // Only add to first matching category
        }
      }
    }

    return groups;
  }, [systemTemplates]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {(['building', 'outdoor', 'interior', 'other'] as TemplateCategory[]).map(cat => (
          <div key={cat} className="space-y-3">
            <Skeleton className="h-5 w-20" />
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Enable or disable system templates. Disabled templates won't appear in dropdowns and won't
        be selected by AI during video generation.
      </div>

      {(['building', 'outdoor', 'interior', 'other'] as TemplateCategory[]).map(category => {
        const templates = templatesByCategory[category];
        if (templates.length === 0) return null;

        return (
          <div key={category} className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {CATEGORY_LABELS[category]}
            </h3>
            <div className="space-y-2">
              {templates.map(template => {
                const isExcluded = excludedIds.includes(template._id);
                const isEnabled = !isExcluded;

                return (
                  <div
                    key={template._id}
                    className={cn(
                      'flex items-center justify-between p-3 rounded-lg border',
                      isExcluded
                        ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 opacity-60'
                        : 'bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className={cn('font-medium text-sm', isExcluded && 'text-gray-500')}
                          >
                            {template.name}
                          </span>
                          {template.flowType === 'extended' && (
                            <Badge variant="outline" className="text-xs">
                              Extended
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={checked => onToggleExclusion(template._id, !checked)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { ManageTemplatesTab };
